import {
  HttpLink,
  ApolloClient,
  InMemoryCache,
  from,
  createQueryPreloader,
  InMemoryCacheConfig,
   split
} from '@apollo/client';
import { setContext } from "@apollo/client/link/context";
import { onError, ErrorResponse } from "@apollo/client/link/error";
import { errorTracker, graphqlErrorHandler } from './graphql-error-handler';
import {getToken} from "@coverright/shared/keycloak";
import { environment } from '@coverright/shared/config';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';


export enum GraphQLErrorType {
  NO_CONNECTION = 'NO_CONNECTION',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  INVALID_EMAIL = 'INVALID_EMAIL',
  ERROR_STATUS = 'ERROR_STATUS'
}

export const getApolloClient = (getTokenFunc: () => Promise<string | undefined>, uri?: string, inMemoryCacheConfig: InMemoryCacheConfig = {}, useWebSocket = false) => {
  const httpLink = new HttpLink({
    uri: uri || environment.graphqlUrl,
    headers: {
      "content-type": "application/json",
    }
  });

  const authLink = setContext(async (_, { headers }: any) => {
    let token: string;
    try {
      token = await getToken() as string
    } catch (e) {
      token = '';
    }
    if (token) {
      return {
        headers: {
          authorization: token ? `Bearer ${token}` : "",
          ...headers,
        }
      };
    } else {
      return {
        headers
      };
    }
  });

  const wsLink = new GraphQLWsLink(createClient({
    url: environment.enrollSubscriptionsUrl,
    connectionParams: async () => {
      const token = await getToken();
      return {
        Authorization: token ? token : "",
      }
    },
    lazy: true,
  }));

  const elink = onError((errResponse: ErrorResponse) => {
    try {
      getToken().then(token => errorTracker(errResponse, token)).catch(() => errorTracker(errResponse));
    } catch (e) {
      errorTracker(errResponse);
    }
    if (graphqlErrorHandler) {
      graphqlErrorHandler(errResponse);
    }
  });

  const requestLink =
    from([
      authLink,
      elink,
      httpLink,
    ])
  ;

  const link = useWebSocket ?
    split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      requestLink
    ):
    requestLink
  ;

  const cache = new InMemoryCache(inMemoryCacheConfig);

  const clientInstance = new ApolloClient({
    link,
    cache,
    connectToDevTools: true
  });
  const preloadQuery = createQueryPreloader(clientInstance);

  return { client: clientInstance, preloadQuery, cache };
}
